import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useCar } from '@/contexts/CarContext';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const GenerateServiceRequestPage: React.FC = () => {
  const { carId: carIdParam } = useParams<{ carId?: string }>();
  const navigate = useNavigate();
  const { cars, selectedCarId } = useCar();
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();

  const [selectedCarIdState, setSelectedCarIdState] = useState<string>(carIdParam || selectedCarId || '');
  const [title, setTitle] = useState<string>('');
  const [ownerNotes, setOwnerNotes] = useState<string>('');
  const [customNotes, setCustomNotes] = useState<string>('');
  const [includedSections, setIncludedSections] = useState<string[]>(['service_history', 'active_issues', 'pending_tasks', 'fluids', 'media']);
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [includeMedia, setIncludeMedia] = useState<boolean>(true);
  const [selectedIssueIds, setSelectedIssueIds] = useState<string[]>([]);
  const [availableIssues, setAvailableIssues] = useState<Array<{ id: string; title?: string; description: string }>>([]);
  const [selectAllIssues, setSelectAllIssues] = useState<boolean>(true);
  const [generating, setGenerating] = useState(false);

  // Set selected car from param or context
  useEffect(() => {
    if (carIdParam) {
      setSelectedCarIdState(carIdParam);
    } else if (selectedCarId) {
      setSelectedCarIdState(selectedCarId);
    } else if (cars.length > 0) {
      setSelectedCarIdState(cars[0].id);
    }
  }, [carIdParam, selectedCarId, cars]);

  // Load available issues when car is selected
  useEffect(() => {
    const loadIssues = async () => {
      if (!selectedCarIdState) {
        setAvailableIssues([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('symptoms')
          .select('id, title, description')
          .eq('car_id', selectedCarIdState)
          .neq('status', 'resolved')
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading issues:', error);
          setAvailableIssues([]);
        } else {
          setAvailableIssues(data || []);
        }
      } catch (error) {
        console.error('Error loading issues:', error);
        setAvailableIssues([]);
      }
    };

    loadIssues();
  }, [selectedCarIdState]);

  const toggleSection = (section: string) => {
    setIncludedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const toggleIssue = (issueId: string) => {
    setSelectedIssueIds(prev =>
      prev.includes(issueId)
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  const handleSelectAllIssues = () => {
    if (selectAllIssues) {
      setSelectedIssueIds([]);
      setSelectAllIssues(false);
    } else {
      setSelectedIssueIds(availableIssues.map(issue => issue.id));
      setSelectAllIssues(true);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCarIdState) {
      toast({
        title: t('serviceRequest.selectCar') || 'Select a car',
        description: t('serviceRequest.selectCarDesc') || 'Please select a car',
        variant: 'destructive',
      });
      return;
    }

    if (!ownerNotes.trim()) {
      toast({
        title: t('serviceRequest.ownerNotesRequired') || 'Owner notes required',
        description: t('serviceRequest.ownerNotesRequiredDesc') || 'Please describe what you want done',
        variant: 'destructive',
      });
      return;
    }

    if (!user) {
      toast({
        title: t('common.error') || 'Error',
        description: t('common.pleaseSignIn') || 'Please sign in',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .insert({
          car_id: selectedCarIdState,
          user_id: user.id,
          title: title.trim() || null,
          owner_notes: ownerNotes.trim(),
          custom_notes: customNotes.trim() || null,
          included_sections: includedSections,
          date_range_start: dateRangeStart || null,
          date_range_end: dateRangeEnd || null,
          include_media: includeMedia,
          selected_issue_ids: selectAllIssues ? [] : selectedIssueIds,
          status: 'active',
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: t('serviceRequest.success') || 'Success',
        description: t('serviceRequest.created') || 'Service request created successfully',
      });

      // Navigate to view the service request
      navigate(`/service-request/view/${data.id}`);
    } catch (error) {
      console.error('Error creating service request:', error);
      toast({
        title: t('serviceRequest.error') || 'Error',
        description: error instanceof Error ? error.message : t('serviceRequest.errorDesc') || 'Failed to create service request',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <PageLayout
      title={t('serviceRequest.generate') || 'Generate Service Request'}
      showBackButton
      onBack={() => navigate(-1)}
    >
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Car selection */}
        <div>
          <Label className="mb-2 block">
            {t('serviceRequest.selectCar') || 'Select Car'}
          </Label>
          <Select
            value={selectedCarIdState}
            onValueChange={setSelectedCarIdState}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('serviceRequest.selectCarPlaceholder') || 'Choose a car'} />
            </SelectTrigger>
            <SelectContent>
              {cars.map((car) => (
                <SelectItem key={car.id} value={car.id}>
                  {car.nickname || `${car.brand} ${car.model} ${car.year}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Optional title */}
        <div>
          <Label className="mb-2 block">
            {t('serviceRequest.title') || 'Title'} <span className="text-gray-500 text-xs">({t('common.optional') || 'Optional'})</span>
          </Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('serviceRequest.titlePlaceholder') || 'e.g., Annual Service'}
          />
        </div>

        {/* Owner notes (required) */}
        <div>
          <Label className="mb-2 block">
            {t('serviceRequest.ownerNotes') || 'What I want done'} *
          </Label>
          <Textarea
            value={ownerNotes}
            onChange={(e) => setOwnerNotes(e.target.value)}
            placeholder={t('serviceRequest.ownerNotesPlaceholder') || 'Describe what you want the mechanic to do...'}
            rows={4}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('serviceRequest.ownerNotesHint') || 'This will appear as non-editable text in the Work Done section'}
          </p>
        </div>

        {/* Custom notes */}
        <div>
          <Label className="mb-2 block">
            {t('serviceRequest.customNotes') || 'Additional Notes'} <span className="text-gray-500 text-xs">({t('common.optional') || 'Optional'})</span>
          </Label>
          <Textarea
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder={t('serviceRequest.customNotesPlaceholder') || 'Any additional information...'}
            rows={3}
          />
        </div>

        {/* Section selector */}
        <div>
          <Label className="mb-3 block">
            {t('serviceRequest.sections.select') || 'Select Sections to Include'}
          </Label>
          <div className="space-y-2">
            {[
              { key: 'service_history', label: t('serviceRequest.sections.serviceHistory') || 'Service History' },
              { key: 'active_issues', label: t('serviceRequest.sections.activeIssues') || 'Active Issues' },
              { key: 'pending_tasks', label: t('serviceRequest.sections.pendingTasks') || 'Pending Tasks' },
              { key: 'fluids', label: t('serviceRequest.sections.fluids') || 'Fluids' },
              { key: 'media', label: t('serviceRequest.sections.media') || 'Media/Photos' },
            ].map((section) => (
              <div key={section.key} className="flex items-center space-x-2">
                <Checkbox
                  id={section.key}
                  checked={includedSections.includes(section.key)}
                  onCheckedChange={() => toggleSection(section.key)}
                />
                <Label
                  htmlFor={section.key}
                  className="text-sm font-normal cursor-pointer"
                >
                  {section.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Date range (only if service_history is included) */}
        {includedSections.includes('service_history') && (
          <div>
            <Label className="mb-2 block">
              {t('serviceRequest.dateRange') || 'Date Range (for Service History)'} <span className="text-gray-500 text-xs">({t('common.optional') || 'Optional'})</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">
                  {t('serviceRequest.dateRangeStart') || 'Start Date'}
                </Label>
                <Input
                  type="date"
                  value={dateRangeStart}
                  onChange={(e) => setDateRangeStart(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">
                  {t('serviceRequest.dateRangeEnd') || 'End Date'}
                </Label>
                <Input
                  type="date"
                  value={dateRangeEnd}
                  onChange={(e) => setDateRangeEnd(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Issue selector (only if active_issues is included) */}
        {includedSections.includes('active_issues') && availableIssues.length > 0 && (
          <div>
            <Label className="mb-2 block">
              {t('serviceRequest.selectIssues') || 'Select Specific Issues'}
            </Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all-issues"
                  checked={selectAllIssues}
                  onCheckedChange={handleSelectAllIssues}
                />
                <Label
                  htmlFor="select-all-issues"
                  className="text-sm font-normal cursor-pointer"
                >
                  {t('serviceRequest.allActiveIssues') || 'All Active Issues'}
                </Label>
              </div>
              {!selectAllIssues && (
                <div className="ml-6 space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {availableIssues.map((issue) => (
                    <div key={issue.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`issue-${issue.id}`}
                        checked={selectedIssueIds.includes(issue.id)}
                        onCheckedChange={() => toggleIssue(issue.id)}
                      />
                      <Label
                        htmlFor={`issue-${issue.id}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {issue.title || issue.description.substring(0, 50)}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Media toggle */}
        {includedSections.includes('media') && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-media"
              checked={includeMedia}
              onCheckedChange={(checked) => setIncludeMedia(checked === true)}
            />
            <Label
              htmlFor="include-media"
              className="text-sm font-normal cursor-pointer"
            >
              {t('serviceRequest.includeMedia') || 'Include Media/Photos'}
            </Label>
          </div>
        )}

        {/* Generate button */}
        <Button
          onClick={handleGenerate}
          disabled={!selectedCarIdState || !ownerNotes.trim() || generating}
          className="w-full bg-lime text-black hover:bg-[#d4e848]"
          size="lg"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('serviceRequest.generating') || 'Generating...'}
            </>
          ) : (
            t('serviceRequest.generateRequest') || 'Generate Service Request'
          )}
        </Button>
      </div>
    </PageLayout>
  );
};

export default GenerateServiceRequestPage;

